import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { NotificationService } from '@app/infrastructure/core/services/notification.service';
import { FileConfig, IFileConfig } from '@app/infrastructure/models/file';
import { FormConfig, IFormConfig } from '@app/infrastructure/models/form/form';
import {
  IQuizBlockDTO,
  QuizBlockDTO,
} from '@app/infrastructure/models/quiz-block';
import { IOptionFile } from '../../quiz-block.component';

export class Answer {
  id: string = '';
  label: string = '';
  correct: boolean = false;
  answer: string = '';
}

export class Image {
  url: string | ArrayBuffer = '';
  id: number = 0;
}

@Component({
  selector: 'app-quiz-options',
  templateUrl: './quiz-options.component.html',
  styleUrls: ['./quiz-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizOptionsComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup = new UntypedFormGroup({});
  @Input() quiz: IQuizBlockDTO = new QuizBlockDTO();
  @Input() imageSavePending: boolean = false;
  @Input() isOptionMoved: boolean = false;
  @Input() public fileConfig: IFileConfig = new FileConfig({
    allowedFileExtensionList: ['png', 'jpg', 'jpeg', 'gif'],
    allowedMimeTypeList: ['image/*'],
    canUploadMultiple: false,
  });
  @Input() public formConfig: IFormConfig = new FormConfig();
  @Output() emitOptionFile = new EventEmitter<IOptionFile>();
  public addText = 'Add Option';
  readonly ctrlName = 'answers';
  public fileId: number = Date.now();
  public imageUrl: Image[] = [];
  public currentImage: string = '';
  public currentImageFile: string = '';

  constructor(
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const formArray = new UntypedFormArray([]);

    if (this.quiz.options) {
      this.quiz.options.forEach((answer, index) => {
        formArray.push(
          this.fb.group({
            id: [`${answer.position}`],
            label: [`Answer ${+answer.position + 1}`],
            correct: [`${answer.correct}` === 'true'], // Being cast to a string even though type is boolean on client and server
            answer: [answer.answer],
            file: [answer.file],
            fileId: [answer.fileId],
            fileLocation: [answer.fileLocation],
            fileBucket: [answer.fileBucket],
            fileName: [answer.fileName],
          }),
        );
        if (answer.fileLocation) {
          this.imageUrl.push({ url: answer.fileLocation, id: index });
        }
      });
    }
    this.form.addControl(this.ctrlName, formArray);
  }

  ngOnDestroy(): void {
    this.form.removeControl(this.ctrlName);
  }

  drop(event: CdkDragDrop<Answer[]>) {
    this.isOptionMoved = true;
    moveItemInArray(
      this.answers.controls,
      event.previousIndex,
      event.currentIndex,
    );
    // option images weren't being dragged since they don't have a formControlName assigned to them, so we rearrange them separately.
    this.dropOptionImages(event.previousIndex, event.currentIndex);
  }

  dropOptionImages(prevInd, currInd) {
    if (this.imageUrl[prevInd] && this.imageUrl[currInd]) {
      [this.imageUrl[prevInd], this.imageUrl[currInd]] = [
        this.imageUrl[currInd],
        this.imageUrl[prevInd],
      ];
      this.cd.detectChanges();
    } else if (!this.imageUrl[prevInd] && this.imageUrl[currInd]) {
      this.imageUrl[prevInd] = { url: '', id: prevInd };
      [this.imageUrl[prevInd], this.imageUrl[currInd]] = [
        this.imageUrl[currInd],
        this.imageUrl[prevInd],
      ];
      this.cd.detectChanges();
    } else if (this.imageUrl[prevInd] && !this.imageUrl[currInd]) {
      this.imageUrl[currInd] = { url: '', id: currInd };
      [this.imageUrl[prevInd], this.imageUrl[currInd]] = [
        this.imageUrl[currInd],
        this.imageUrl[prevInd],
      ];
      this.cd.detectChanges();
    }
  }

  getImageSrc(index) {
    this.imageUrl?.map((image) => {
      if (image.id === index) {
        return image.url;
      }
    });
    return this.imageUrl[index]?.url;
  }
  hasImage(index: number): boolean {
    let optionHasImage: boolean;
    this.quiz.options?.forEach((option) => {
      if (+option.position === index) {
        optionHasImage = option.fileLocation ? true : false;
      }
    });
    if (!this.isOptionMoved) {
      return optionHasImage || this.imageUrl[index]?.id === index;
    } else {
      return this.imageUrl[index]?.url === '' ? false : true;
    }
  }

  get answers() {
    return this.form.controls[this.ctrlName] as UntypedFormArray;
  }

  addOption(event: any) {
    this.isOptionMoved = false;
    const answerForm = this.fb.group({
      id: [`${this.answers.length}`],
      label: [`Answer ${this.answers.length + 1}`],
      correct: [false],
      answer: [''],
      file: [],
      fileId: [''],
      fileLocation: [''],
      fileBucket: [''],
      fileName: [''],
    });
    this.answers.push(answerForm);
  }

  delete(index: number) {
    this.isOptionMoved = false;
    this.answers.removeAt(index);
    this.imageUrl = this.imageUrl.filter((img, i) => i !== index);
    this.imageUrl.forEach((image, i) => (image.id = i));
  }

  private previewImageUpload(fileList: FileList, index: number): void {
    if (fileList && fileList[0]) {
      const file = fileList[0];
      const mimeType = file.type;
      if (mimeType.match(/image\/*/) !== null) {
        if (fileList.length === 1) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = (event) => {
            this.imageUrl.push({ url: event.target.result, id: index });
            this.cd.markForCheck();
            this.cd.detectChanges();
          };
        }
      }
    }
  }

  public onFileSelect(fileList: FileList, index: number): void {
    if (!this.fileConfig.canUploadMultiple && fileList?.length > 1) {
      this.notificationService.showError(['Please select only one file.']);
    } else if (fileList[0]) {
      this.previewImageUpload(fileList, index);
      this.emitOptionFile.emit({ position: index, file: fileList[0] });
    }
  }
}
